import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress
} from '@material-ui/core';
import { FilterList as FilterIcon, Clear as ClearIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';

const useStyles = makeStyles(theme => ({
  filterSection: {
    marginBottom: theme.spacing(3),
  },
  filterTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  formControl: {
    minWidth: 120,
    marginBottom: theme.spacing(1),
  },
  dateContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  dateInput: {
    '& input[type="date"]': {
      padding: '10.5px 14px',
    },
  },
}));

const KanbanFilters = ({ open, onClose, filters = {}, onApplyFilters }) => {
  const classes = useStyles();
  const [localFilters, setLocalFilters] = useState({
    priority: [],
    status: [],
    assignees: [],
    tags: [],
    dateRange: {
      start: '',
      end: '',
    },
    showOverdue: false,
    showUnassigned: false,
    showCompleted: false,
    searchText: '',
  });
  
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalFilters(prevFilters => ({ ...prevFilters, ...filters }));
      fetchFilterData();
    }
  }, [filters, open]);
  
  const fetchFilterData = async () => {
    setLoadingData(true);
    try {
      // Buscar usuários disponíveis
      const [usersResponse, tagsResponse] = await Promise.all([
        api.get('/users'),
        api.get('/tag/kanban/')
      ]);
      
      setAvailableUsers(usersResponse.data.users || usersResponse.data || []);
      setAvailableTags(tagsResponse.data.lista || tagsResponse.data || []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleArrayFilterChange = (filterType, value) => {
    setLocalFilters(prev => {
      const currentArray = prev[filterType] || [];
      const isObjectValue = typeof value === 'object' && value !== null;
      const valueId = isObjectValue ? value.id : value;
      
      const exists = currentArray.some(item => 
        typeof item === 'object' ? item.id === valueId : item === valueId
      );
      
      const newArray = exists
        ? currentArray.filter(item => 
            typeof item === 'object' ? item.id !== valueId : item !== valueId
          )
        : [...currentArray, value];
      
      return {
        ...prev,
        [filterType]: newArray,
      };
    });
  };
  
  const handleAssigneesChange = (event, newValue) => {
    setLocalFilters(prev => ({
      ...prev,
      assignees: newValue
    }));
  };
  
  const handleTagsChange = (event, newValue) => {
    setLocalFilters(prev => ({
      ...prev,
      tags: newValue
    }));
  };

  const handleDateRangeChange = (type, date) => {
    setLocalFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [type]: date,
      },
    }));
  };

  const handleApplyFilters = () => {
    // Processar filtros para formato da API
    const processedFilters = {
      ...localFilters,
      assigneeId: localFilters.assignees.map(user => user.id || user).join(','),
      tagIds: localFilters.tags.map(tag => tag.id || tag).join(','),
      dueDateStart: localFilters.dateRange.start || null,
      dueDateEnd: localFilters.dateRange.end || null,
      includeCompleted: localFilters.showCompleted,
      searchParam: localFilters.searchText || null
    };
    
    onApplyFilters(processedFilters);
    onClose();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      priority: [],
      status: [],
      assignees: [],
      tags: [],
      dateRange: {
        start: '',
        end: '',
      },
      showOverdue: false,
      showUnassigned: false,
      showCompleted: false,
      searchText: '',
    };
    setLocalFilters(clearedFilters);
    onApplyFilters(clearedFilters);
  };

  const priorityOptions = [
    { value: 'low', label: i18n.t('kanban.tasks.priority.low') },
    { value: 'medium', label: i18n.t('kanban.tasks.priority.medium') },
    { value: 'high', label: i18n.t('kanban.tasks.priority.high') },
    { value: 'urgent', label: i18n.t('kanban.tasks.priority.urgent') },
  ];

  const statusOptions = [
    { value: 'todo', label: i18n.t('kanban.tasks.status.todo') },
    { value: 'in_progress', label: i18n.t('kanban.tasks.status.in_progress') },
    { value: 'done', label: i18n.t('kanban.tasks.status.done') },
    { value: 'blocked', label: i18n.t('kanban.tasks.status.blocked') },
  ];

  const hasActiveFilters = () => {
    return (
      localFilters.priority.length > 0 ||
      localFilters.status.length > 0 ||
      localFilters.assignees.length > 0 ||
      localFilters.tags.length > 0 ||
      localFilters.dateRange.start ||
      localFilters.dateRange.end ||
      localFilters.showOverdue ||
      localFilters.showUnassigned ||
      localFilters.showCompleted ||
      localFilters.searchText.length > 0
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">{i18n.t('kanban.filters.advancedFilters')}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.filterSection}>
          <TextField
            fullWidth
            label={i18n.t('kanban.searchPlaceholder')}
            value={localFilters.searchText}
            onChange={(e) => handleFilterChange('searchText', e.target.value)}
            variant="outlined"
            size="small"
          />
        </Box>

        <Box className={classes.filterSection}>
          <Typography className={classes.filterTitle}>
            {i18n.t('kanban.filters.priority')}
          </Typography>
          <Grid container spacing={1}>
            {priorityOptions.map(option => (
              <Grid item key={option.value}>
                <Chip
                  label={option.label}
                  clickable
                  color={localFilters.priority.includes(option.value) ? 'primary' : 'default'}
                  onClick={() => handleArrayFilterChange('priority', option.value)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className={classes.filterSection}>
          <Typography className={classes.filterTitle}>
            {i18n.t('kanban.filters.taskStatus')}
          </Typography>
          <Grid container spacing={1}>
            {statusOptions.map(option => (
              <Grid item key={option.value}>
                <Chip
                  label={option.label}
                  clickable
                  color={localFilters.status.includes(option.value) ? 'primary' : 'default'}
                  onClick={() => handleArrayFilterChange('status', option.value)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className={classes.filterSection}>
          <Typography className={classes.filterTitle}>
            {i18n.t('kanban.filters.dateRange')}
          </Typography>
          <Box className={classes.dateContainer}>
            <TextField
              type="date"
              label={i18n.t('kanban.filters.startDate')}
              value={localFilters.dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              variant="outlined"
              size="small"
              className={classes.dateInput}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              type="date"
              label={i18n.t('kanban.filters.endDate')}
              value={localFilters.dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              variant="outlined"
              size="small"
              className={classes.dateInput}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </Box>

        <Divider style={{ margin: '16px 0' }} />

        <Box className={classes.filterSection}>
          <Typography className={classes.filterTitle}>
            {i18n.t('kanban.filters.quickFilters')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={localFilters.showOverdue}
                onChange={(e) => handleFilterChange('showOverdue', e.target.checked)}
              />
            }
            label={i18n.t('kanban.card.overdue')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={localFilters.showUnassigned}
                onChange={(e) => handleFilterChange('showUnassigned', e.target.checked)}
              />
            }
            label={i18n.t('kanban.filters.unassigned')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={localFilters.showCompleted}
                onChange={(e) => handleFilterChange('showCompleted', e.target.checked)}
              />
            }
            label={i18n.t('kanban.filters.completed')}
          />
        </Box>

        {hasActiveFilters() && (
          <Box className={classes.filterSection}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t('kanban.filters.activeFiltersCount', { count: Object.values(localFilters).flat().filter(Boolean).length })}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClearFilters} disabled={!hasActiveFilters()}>
          {i18n.t('kanban.filters.clearFilters')}
        </Button>
        <Button onClick={onClose}>
          {i18n.t('kanban.cancel')}
        </Button>
        <Button onClick={handleApplyFilters} color="primary" variant="contained">
          {i18n.t('kanban.filters.applyFilters')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanFilters;